import { ChevronDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Menu, menuItemClass } from "@/components/ui/menu";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

export interface NavTab {
  key: string;
  label: string;
  to: string;
  icon?: LucideIcon;
}

/** `gap-1` between tabs, in pixels — the measurement has to know it. */
const GAP = 4;

/**
 * A tab strip that keeps what fits and puts the rest behind "More".
 *
 * The alternative when the tabs outgrow the line is to scroll them sideways,
 * which is what this replaces: a sideways scroller hides sections behind a
 * gesture nothing on the page advertises, and its overflow drags the page along
 * with it. Here every section is one click away at any width — the ones that
 * fit are on the line, the rest are in the menu at the end of it.
 *
 * The widths come from a copy of the strip rendered out of view, so they are
 * the tabs' natural widths and not the widths of whatever is on screen now —
 * measuring the visible row would feed the answer back into the question. The
 * current tab is always on the line: if it would fall into the menu it takes
 * the last visible place instead, because the one tab a person must be able to
 * see is the one they are on.
 */
export function NavTabs({
  items,
  current,
  label,
  className,
}: {
  items: readonly NavTab[];
  /** The `key` of the tab in view. */
  current: string;
  /** Names the strip for a screen reader. */
  label: string;
  className?: string;
}) {
  const t = useTranslation();
  const line = useRef<HTMLDivElement>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const widths = useRef<Record<string, number>>({});
  const moreWidth = useRef(0);
  const [visible, setVisible] = useState(items.length);

  const measure = useCallback(() => {
    const room = line.current?.clientWidth ?? 0;
    // Nothing to measure against while the strip is display:none — at `lg` the
    // rail takes over — so keep whatever was worked out when it was on screen.
    if (room === 0) return;
    let used = 0;
    let fits = 0;
    for (const [index, item] of items.entries()) {
      const next = used + (index === 0 ? 0 : GAP) + (widths.current[item.key] ?? 0);
      // Every tab but the last has to leave room for the "More" trigger.
      const reserve = index === items.length - 1 ? 0 : GAP + moreWidth.current;
      if (next + reserve > room) break;
      used = next;
      fits += 1;
    }
    // One tab always shows, however narrow the screen: an empty line above a
    // menu is not a tab strip.
    setVisible(Math.max(1, fits));
  }, [items]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    const target = line.current;
    const copy = ruler.current;
    if (!target || !copy) return;
    // The line for the room available, the copy for the labels themselves —
    // a translation or a late font changes what they need, not what there is.
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    observer.observe(copy);
    return () => observer.disconnect();
  }, [measure]);

  const shown = items.slice(0, visible);
  const hidden = items.slice(visible);
  // The current tab keeps its place on the line by taking the last one.
  if (hidden.some((item) => item.key === current)) {
    const moved = hidden.find((item) => item.key === current)!;
    shown[shown.length - 1] = moved;
    hidden.splice(hidden.indexOf(moved), 1, items[visible - 1]!);
  }

  return (
    <div className={cn("hairline-b min-w-0", className)}>
      {/* The strip as it would be with every tab on the line, out of sight and
          purely to be measured.

          `invisible` hides it but leaves it in the page's scrollable area, and
          a row of every tab is wider than a phone — which is a horizontal
          scrollbar across the whole page. So it is clipped instead: a box of no
          size with `overflow: hidden`, holding a row at its natural width. The
          row is still laid out, so it still measures; it just has nowhere to
          overflow to. */}
      <div
        aria-hidden
        className="pointer-events-none invisible absolute h-0 w-0 overflow-hidden"
      >
        <div ref={ruler} className="flex w-max gap-1 whitespace-nowrap">
          {items.map((item) => (
            <span
              key={item.key}
              ref={(node) => {
                if (node) widths.current[item.key] = node.offsetWidth;
              }}
              className={tabClass(false)}
            >
              {item.icon ? <item.icon className="size-4.5 shrink-0" aria-hidden /> : null}
              {item.label}
            </span>
          ))}
          <span
            ref={(node) => {
              if (node) moreWidth.current = node.offsetWidth;
            }}
            className={tabClass(false)}
          >
            {t("nav.more")}
            <ChevronDown className="size-4" aria-hidden />
          </span>
        </div>
      </div>

      <nav ref={line} aria-label={label} className="-mb-px flex min-w-0 items-end gap-1">
        {shown.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            aria-current={item.key === current ? "page" : undefined}
            className={cn(tabClass(item.key === current), "min-w-0")}
          >
            {item.icon ? <item.icon className="size-4.5 shrink-0" aria-hidden /> : null}
            <span className="truncate">{item.label}</span>
          </Link>
        ))}
        {hidden.length > 0 ? (
          <Menu
            className="shrink-0"
            width="w-56"
            trigger={
              <span className={cn(tabClass(false), "whitespace-nowrap")}>
                {t("nav.more")}
                <ChevronDown
                  className="size-4 transition-transform group-open:rotate-180"
                  aria-hidden
                />
              </span>
            }
          >
            {hidden.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                role="menuitem"
                aria-current={item.key === current ? "page" : undefined}
                className={menuItemClass()}
              >
                {item.icon ? <item.icon className="size-4 shrink-0" aria-hidden /> : null}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            ))}
          </Menu>
        ) : null}
      </nav>
    </div>
  );
}

/** One tab, selected or not — shared with the copy that is only measured. */
function tabClass(selected: boolean) {
  return cn(
    "text-callout flex shrink-0 items-center gap-2 rounded-t-md px-3 pt-2 pb-2.5",
    "border-b-2 font-medium transition-colors duration-[var(--duration-fast)]",
    selected
      ? "border-accent text-fg [&_svg]:text-accent"
      : "text-fg-muted hover:text-fg border-transparent [&_svg]:text-fg-subtle",
  );
}
